// Pipeline estándar del sistema para vacantes ATS.
// Es el fallback cuando el proyecto no tiene plantillas de pipeline
// (customFields.atsPipelineTemplates) o ninguna está marcada como
// predeterminada. Se usa en el wizard de vacantes y como punto de partida
// al crear una plantilla nueva en Configuración.
// Devuelve siempre un objeto nuevo para evitar mutaciones compartidas.
export const buildDefaultPipeline = () => ({
  currentPhaseId: 'search',
  phases: [
    {
      id: 'search',
      name: 'Búsqueda activa',
      type: 'search',
      order: 1,
      stages: [
        { id: 'new', name: 'Nuevo', type: 'new', order: 1, hasModules: true, modules: [] },
        { id: 'screening', name: 'Screening', type: 'screening', order: 2, hasModules: true, modules: [] },
      ],
    },
    {
      id: 'evaluation',
      name: 'Evaluación',
      type: 'evaluation',
      order: 2,
      stages: [
        { id: 'evaluation_stage', name: 'Evaluaciones', type: 'evaluation', order: 1, hasModules: true, modules: [] },
        { id: 'interview_consultant', name: 'Entrevista consultor', type: 'interview_consultant', order: 2, hasModules: true, modules: [] },
      ],
    },
    {
      id: 'shortlist',
      name: 'Shortlist',
      type: 'shortlist',
      order: 3,
      stages: [
        { id: 'interview_client', name: 'Entrevista cliente', type: 'interview_client', order: 1, hasModules: true, modules: [] },
        { id: 'shortlist_stage', name: 'Shortlist', type: 'shortlist', order: 2, hasModules: false, modules: [] },
      ],
    },
    {
      id: 'negotiation',
      name: 'Negociación',
      type: 'negotiation',
      order: 4,
      stages: [
        { id: 'offer', name: 'Oferta', type: 'offer', order: 1, hasModules: false, modules: [] },
        { id: 'hired', name: 'Contratado', type: 'hired', order: 2, hasModules: false, modules: [] },
      ],
    },
  ],
});

// Pipeline por defecto para vacantes de RECLUTAMIENTO MASIVO (tipo 'massive').
// Calca el flujo del plan (PLAN_RECLUTAMIENTO_MASIVO.md §4.1): el bot de
// captación deposita candidatos en "Nuevo" (handoff, F3) y el consultor
// trabaja las etapas siguientes. Etapas con comportamiento especial existente
// reutilizan sus types (new, screening, interview_client, hired); el resto va
// como 'custom'.
export const buildMassivePipeline = () => ({
  currentPhaseId: 'captados',
  phases: [
    {
      id: 'captados',
      name: 'Captados',
      type: 'search',
      order: 1,
      stages: [
        { id: 'new', name: 'Nuevo', type: 'new', order: 1, hasModules: true, modules: [] },
      ],
    },
    {
      id: 'validacion',
      name: 'Validación',
      type: 'evaluation',
      order: 2,
      stages: [
        // Columna con el módulo "Verificación de antecedentes" (botón Verificar
        // antecedentes, Chapi): reemplaza a la columna "Revisión de antecedentes"
        // sin lógica (PLAN_MASIVO_PORTAL)
        // Consulta automática al llegar a la columna encendida por defecto en
        // las masivas (2026-10-13; switch en el engranaje de la etapa)
        { id: 'background_check', name: 'Verificación de antecedentes', type: 'custom', order: 1, hasModules: true, modules: ['background_check'], backgroundCheckAuto: true },
        // Columna con el módulo "Solicitud de documentos" (solicitud masiva y
        // estado de los documentos; PLAN_SOLICITUD_DOCUMENTOS_MASIVA, 2026-10-14).
        // Solo vacantes nuevas: las masivas ya creadas no se tocan.
        { id: 'documents', name: 'Documentos', type: 'custom', order: 2, hasModules: true, modules: ['document_request'] },
      ],
    },
    {
      id: 'screening_phase',
      name: 'Screening',
      type: 'evaluation',
      order: 3,
      stages: [
        { id: 'phone_screening', name: 'Screening telefónico', type: 'screening', order: 1, hasModules: true, modules: [] },
      ],
    },
    {
      id: 'cliente',
      name: 'Cliente',
      type: 'shortlist',
      order: 4,
      stages: [
        { id: 'interview_client', name: 'Entrevista cliente', type: 'interview_client', order: 1, hasModules: true, modules: [] },
        { id: 'medical_exams', name: 'Exámenes preocupacionales', type: 'custom', order: 2, hasModules: false, modules: [] },
      ],
    },
    {
      id: 'cierre',
      name: 'Cierre',
      type: 'negotiation',
      order: 5,
      stages: [
        { id: 'hired', name: 'Contratado', type: 'hired', order: 1, hasModules: false, modules: [] },
        { id: 'not_selected', name: 'No seleccionado', type: 'custom', order: 2, hasModules: false, modules: [] },
      ],
    },
  ],
});

/**
 * ¿La etapa tiene la verificación de antecedentes? Es un módulo de la etapa
 * (PLAN_MASIVO_PORTAL); también se reconoce la etapa tipo 'background_check'
 * de las vacantes de prueba creadas antes del cambio.
 */
export const stageHasBackgroundCheck = (stage) =>
  Boolean(stage) && ((stage.modules || []).includes('background_check') || stage.type === 'background_check');

/** ¿El pipeline tiene la verificación de antecedentes en alguna etapa? */
export const hasBackgroundCheckStage = (pipeline) =>
  (pipeline?.phases || []).some((ph) => (ph.stages || []).some(stageHasBackgroundCheck));

// Columna dedicada a la verificación: la de la plantilla masiva (o la etapa
// de prueba previa). Fuera de Chile se quita entera; en otras etapas solo se
// desmarca el módulo.
const isBackgroundCheckColumn = (s) =>
  stageHasBackgroundCheck(s) && (s.id === 'background_check' || s.type === 'background_check');

/**
 * El mismo pipeline sin la verificación de antecedentes: fuera de Chile está
 * en espera (PLAN_MASIVO_PORTAL). Las etapas de la fase que la tenía se
 * renumeran; el resto queda igual.
 */
export const withoutBackgroundCheck = (pipeline) => ({
  ...pipeline,
  phases: (pipeline?.phases || []).map((ph) => {
    const stages = ph.stages || [];
    if (!stages.some(stageHasBackgroundCheck)) return ph;
    return {
      ...ph,
      stages: stages
        .filter((s) => !isBackgroundCheckColumn(s))
        .map(({ backgroundCheckAuto, ...s }, i) => ({
          ...s,
          order: i + 1,
          ...(Array.isArray(s.modules) ? { modules: s.modules.filter((m) => m !== 'background_check') } : {}),
        })),
    };
  }),
});

// Construye el pipeline de una vacante a partir de una plantilla guardada
// (copia profunda: la vacante no queda vinculada a la plantilla).
export const pipelineFromTemplate = (template) => {
  const phases = JSON.parse(JSON.stringify(template?.pipeline?.phases || []));
  return {
    currentPhaseId: phases[0]?.id || null,
    phases,
  };
};

export default buildDefaultPipeline;
